import { TokenRedemptionOptionType } from '@daymade/common-lib/dbEnums';
import { ZDrawTypeNumberSetup } from '@daymade/common-lib/dbJsonModels';
import { ensureSubsetOfKeys } from '@daymade/common-lib/types';
import { z } from 'zod';

import { ZDrawScheduleSelect, ZDrawSelect } from '../draw/draw';
import { ZDrawOrderSelect } from '../draw/drawOrder';
import {
  zodInsertTokenRedemption,
  zodSelectOrder,
  zodSelectTokenOrder,
  zodSelectTokenRedemption,
  zodSelectTokenRedemptionOption,
} from '../generated/zodSchemas.generated';
import { ZOrderSelect } from '../order';
import { ZAccountJournalSelectPrivate } from '../payment/models';
import { ZPrizeSelect, ZProductSelect } from '../prize';
import { ZTreeLogSelect } from '../tree/models';
import { ZPrizeOrderSelect } from '../winner/models';

export const TokenCostInGBP = 50;

export const TokenRedemptionOptionTypeEnum = TokenRedemptionOptionType;

export const ZTokenRedemptionOptionSelect =
  zodSelectTokenRedemptionOption.extend(
    ensureSubsetOfKeys(zodSelectTokenRedemptionOption.shape, {
      createdAt: z.coerce.date(),
      updatedAt: z.coerce.date(),
    }),
  );

export const ZTokenRedemptionOptionPrize = ZPrizeSelect.pick({
  id: true,
  slug: true,
  type: true,
  title: true,
  subheading: true,
  overviewBody: true,
  whatsIncludedBody: true,
  fulfilmentInfo: true,
  imageLocations: true,
  requiresDeliveryAddress: true,
  requiresEmailAddress: true,
  requiresPhoneNumber: true,
  additionalQuestionsForPlayer: true,
}).extend({
  products: ZProductSelect.pick({
    id: true,
    name: true,
    description: true,
    imageLocation: true,
  }).array(),
});

export const ZTokenRedemptionOptionSelectResponse =
  ZTokenRedemptionOptionSelect.pick({
    id: true,
    code: true,
    slug: true,
    type: true,
    price: true,
    title: true,
    description: true,
    heroImage: true,
    group: true,
    groupOrder: true,
  }).extend({
    drawDetails: z
      .object({
        gameType: z.string().nullable(),
        group: z.string().nullable(),
        drawTypeCode: z.string(),
        numberSetup: ZDrawTypeNumberSetup.nullable(),
        schedule: z.string().nullable(),
        nextDrawId: z.number().nullable(),
        nextDrawDate: z.coerce.date().nullable(),
        numEntries: z.number().nullable(),
        entryCap: z.number().nullable(),
        maxPlayerEntries: z.number().nullable(),
        prize: ZPrizeSelect.pick({
          id: true,
          slug: true,
          title: true,
          subheading: true,
          overviewBody: true,
          whatsIncludedBody: true,
          imageLocations: true,
        }).nullable(),
      })
      .optional(),
    prizes: ZTokenRedemptionOptionPrize.array().optional(),
  });

export const ZTokenRedemptionOptionsItem = ZTokenRedemptionOptionSelect.pick({
  id: true,
  code: true,
  slug: true,
  type: true,
  price: true,
  title: true,
  description: true,
  heroImage: true,
  group: true,
  groupOrder: true,
  status: true,
}).extend({
  drawDetails: z
    .object({
      gameType: z.string().nullable(),
      group: z.string().nullable(),
      drawTypeCode: z.string().nullable(),
      numberSetup: ZDrawTypeNumberSetup.nullable(),
      schedule: z.string().nullable(),
      nextDrawDate: z.coerce.date().nullable(),
      numEntries: z.number().nullable(),
      entryCap: z.number().nullable(),
      maxPlayerEntries: z.number().nullable(),
      prizeTitle: z.string().nullable(),
      prizeSubheading: z.string().nullable(),
      prizeImageLocation: z.string().nullable(),
    })
    .optional(),
  prizes: ZTokenRedemptionOptionPrize.array().optional(),
});

export const ZTokenRedemptionSelect = zodSelectTokenRedemption.extend(
  ensureSubsetOfKeys(zodSelectTokenRedemption.shape, {
    orderId: z.optional(z.string().nullable()),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
  }),
);

export const ZTokenRedemptionInsert = zodInsertTokenRedemption;

export const ZTokenRedemptionOrder = z.object({
  tokenRedemption: ZTokenRedemptionSelect.pick({
    id: true,
    status: true,
    playerId: true,
    tokenRedemptionOptionId: true,
  }),
  order: ZOrderSelect,
  drawOrder: z.optional(ZDrawOrderSelect.nullable()),
  draw: z.optional(ZDrawSelect.nullable()),
  drawSchedule: z.optional(ZDrawScheduleSelect.nullable()),
  prize: z.optional(ZPrizeSelect.nullable()),
  prizeOrder: z.optional(ZPrizeOrderSelect.nullable()),
  treeLog: z.optional(ZTreeLogSelect.nullable()),
});

export type TTokenRedemptionOptionSelectResponse = z.infer<
  typeof ZTokenRedemptionOptionSelectResponse
>;

export type TTokenRedemptionOptionPrize = z.infer<
  typeof ZTokenRedemptionOptionPrize
>;

export const ZPurchaseTokensResponse = z.object({
  tokenOrder: zodSelectTokenOrder
    .pick({
      id: true,
      createdAt: true,
      status: true,
      tokenAmount: true,
      playerId: true,
      orderId: true,
    })
    .extend(
      ensureSubsetOfKeys(zodSelectTokenOrder.shape, {
        createdAt: z.coerce.date(),
      }),
    ),
  order: zodSelectOrder
    .pick({
      id: true,
      createdAt: true,
      status: true,
      playerId: true,
      isSubscription: true,
      totalFiatAmount: true,
      tokenAmount: true,
      paymentMethod: true,
      subscriptionId: true,
    })
    .extend(
      ensureSubsetOfKeys(zodSelectOrder.shape, { createdAt: z.coerce.date() }),
    ),
  adyenPaymentSession: z.optional(
    z.object({
      id: z.string(),
      sessionData: z.string(),
      adyenPaymentSessionId: z.string(),
    }),
  ),
});

export const ZGetTokenTransactionResponse = ZAccountJournalSelectPrivate.pick({
  id: true,
  refType: true,
  refId: true,
})
  .extend({
    createdAt: z.coerce.date(),
    amount: z.number(),
    notes: z.string(),
  })
  .array();
